import { STRUCTURE_GRID_SIZE } from 'data/constants';
import { GridSpaceDataType } from 'data/types';
import React, { useMemo } from 'react';
import {
  angledEdgeDragAxis,
  EdgeCardinal,
  getBboxCornerForVertex,
  getExteriorPolygonEdges,
  isEdgeAxisAligned,
  isRectangularVertices,
  RelativeVertex
} from 'utils/space-polygon';
import {
  getEdgeStrokeColor,
  getInsetEdgeEndpoints,
  getVertexFillColor
} from './polygon-handles-helpers';
import {
  PolygonHandleActiveState,
  PolygonHandleHoverState
} from './polygon-handles-types';

type PolygonHandlesProps = {
  gridSpace: GridSpaceDataType;
  hoverState: PolygonHandleHoverState;
  activeState: PolygonHandleActiveState;
  isSpaceHovered: boolean;
  zoom: number;
  contextMenuEdgeIndex?: number | null;
  dropEdgeCardinal?: EdgeCardinal | null;
  dropEdgeIndex?: number | null;
  onVertexMouseDown: (e: React.MouseEvent, vertexIndex: number) => void;
  onEdgeMouseDown: (e: React.MouseEvent, edgeIndex: number) => void;
  onVertexHoverChange: (vertexIndex: number | null) => void;
  onEdgeHoverChange: (edgeIndex: number | null) => void;
  onEdgeContextMenu?: (
    e: React.MouseEvent,
    edgeIndex: number,
    cardinal: 'top' | 'right' | 'bottom' | 'left' | null
  ) => void;
  onVertexContextMenu?: (e: React.MouseEvent, vertexIndex: number) => void;
};

type EdgeRenderItem = {
  index: number;
  p1: RelativeVertex;
  p2: RelativeVertex;
  hitP1: RelativeVertex;
  hitP2: RelativeVertex;
  cursor: string;
  cardinal: 'top' | 'right' | 'bottom' | 'left' | null;
};

type VertexRenderItem = {
  index: number;
  position: RelativeVertex;
  cursor: string;
};

const EDGE_HIT_STROKE = 10;
const EDGE_VISIBLE_STROKE = 2;
const VERTEX_HANDLE_SIZE = 10;

function pixelVerticesToFeet(vertices: RelativeVertex[]): RelativeVertex[] {
  return vertices.map((v) => ({
    x: v.x / STRUCTURE_GRID_SIZE,
    y: v.y / STRUCTURE_GRID_SIZE
  }));
}

function bboxCornerCursor(corner: 'nw' | 'ne' | 'sw' | 'se' | null): string {
  switch (corner) {
    case 'nw':
      return 'nw-resize';
    case 'ne':
      return 'ne-resize';
    case 'sw':
      return 'sw-resize';
    case 'se':
      return 'se-resize';
    default:
      return 'move';
  }
}

function axisAlignedCursor(p1: RelativeVertex, p2: RelativeVertex): string {
  return Math.abs(p1.x - p2.x) < 1e-3 ? 'ew-resize' : 'ns-resize';
}

function angledCursor(axis: 'horizontal' | 'vertical'): string {
  return axis === 'horizontal' ? 'ew-resize' : 'ns-resize';
}

function PolygonHandles({
  gridSpace,
  hoverState,
  activeState,
  isSpaceHovered,
  zoom,
  contextMenuEdgeIndex,
  dropEdgeCardinal,
  dropEdgeIndex: placementEdgeIndex = null,
  onVertexMouseDown,
  onEdgeMouseDown,
  onVertexHoverChange,
  onEdgeHoverChange,
  onEdgeContextMenu,
  onVertexContextMenu
}: PolygonHandlesProps) {
  const verticesPixels = gridSpace.vertices ?? [];
  const widthPixels = gridSpace.position.width;
  const heightPixels = gridSpace.position.height;
  const safeZoom = zoom > 0 ? zoom : 1;
  const vertexHandleSize = VERTEX_HANDLE_SIZE / safeZoom;
  const vertexStrokeWidth = 1 / safeZoom;

  const { edges, vertices } = useMemo(() => {
    if (verticesPixels.length < 3) {
      return {
        edges: [] as EdgeRenderItem[],
        vertices: [] as VertexRenderItem[]
      };
    }

    const verticesFeet = pixelVerticesToFeet(verticesPixels);
    const widthFeet = widthPixels / STRUCTURE_GRID_SIZE;
    const heightFeet = heightPixels / STRUCTURE_GRID_SIZE;
    const isRectangle = isRectangularVertices(
      verticesFeet,
      widthFeet,
      heightFeet
    );
    const cardinalByEdgeIndex = new Map(
      getExteriorPolygonEdges(verticesFeet, null).map((edge) => [
        edge.edgeIndex,
        edge.cardinal
      ])
    );

    const edgeItems: EdgeRenderItem[] = verticesPixels.map((p1, i) => {
      const p2 = verticesPixels[(i + 1) % verticesPixels.length];
      const axisAligned = isEdgeAxisAligned(verticesFeet, i);
      let cursor: string;
      if (axisAligned) {
        cursor = axisAlignedCursor(p1, p2);
      } else {
        cursor = angledCursor(angledEdgeDragAxis(verticesFeet, i));
      }
      const cardinal = cardinalByEdgeIndex.get(i) ?? null;
      const { p1: hitP1, p2: hitP2 } = axisAligned
        ? getInsetEdgeEndpoints(p1, p2, EDGE_HIT_STROKE / 2)
        : { p1, p2 };
      return { index: i, p1, p2, hitP1, hitP2, cursor, cardinal };
    });

    const vertexItems: VertexRenderItem[] = verticesPixels.map(
      (position, i) => {
        const corner = isRectangle
          ? getBboxCornerForVertex(verticesFeet, i, widthFeet, heightFeet)
          : null;
        return {
          index: i,
          position,
          cursor: bboxCornerCursor(corner)
        };
      }
    );

    return { edges: edgeItems, vertices: vertexItems };
  }, [verticesPixels, widthPixels, heightPixels]);

  const dropEdgeIndex = useMemo(() => {
    if (placementEdgeIndex !== null) return placementEdgeIndex;
    if (!dropEdgeCardinal || edges.length === 0) return null;
    const horizontal =
      dropEdgeCardinal === 'top' || dropEdgeCardinal === 'bottom';
    let bestIndex: number | null = null;
    let bestProjection = 0;
    for (const edge of edges) {
      if (edge.cardinal === dropEdgeCardinal) {
        const projection = horizontal
          ? Math.abs(edge.p2.x - edge.p1.x)
          : Math.abs(edge.p2.y - edge.p1.y);
        if (projection > bestProjection) {
          bestProjection = projection;
          bestIndex = edge.index;
        }
      }
    }
    return bestIndex;
  }, [edges, dropEdgeCardinal, placementEdgeIndex]);

  if (verticesPixels.length < 3 || widthPixels <= 0 || heightPixels <= 0) {
    return null;
  }

  return (
    <svg
      className='pointer-events-none absolute inset-0 z-[15] h-full w-full overflow-visible'
      viewBox={`0 0 ${widthPixels} ${heightPixels}`}
      preserveAspectRatio='none'
    >
      {edges.map((edge) => {
        const isHover = hoverState.edgeIndex === edge.index;
        const isActive = activeState.edgeIndex === edge.index;
        const isContextMenuActive = contextMenuEdgeIndex === edge.index;
        const isDropTarget = edge.index === dropEdgeIndex;
        const visibleStroke = getEdgeStrokeColor({
          isActive,
          isContextMenuActive,
          isHover,
          isDropTarget
        });
        let visibleStrokeWidth = EDGE_VISIBLE_STROKE;
        if (isDropTarget || isContextMenuActive) {
          visibleStrokeWidth = 4;
        }
        let visibleStrokeOpacity = 0.7;
        if (isDropTarget) {
          visibleStrokeOpacity = 0.85;
        } else if (isContextMenuActive) {
          visibleStrokeOpacity = 1;
        }
        return (
          <g key={`edge-${edge.index}`}>
            <line
              x1={edge.p1.x}
              y1={edge.p1.y}
              x2={edge.p2.x}
              y2={edge.p2.y}
              stroke={visibleStroke}
              strokeWidth={visibleStrokeWidth}
              strokeOpacity={visibleStrokeOpacity}
              style={{ pointerEvents: 'none' }}
            />
            <line
              x1={edge.hitP1.x}
              y1={edge.hitP1.y}
              x2={edge.hitP2.x}
              y2={edge.hitP2.y}
              stroke='transparent'
              strokeWidth={EDGE_HIT_STROKE}
              style={{ cursor: edge.cursor, pointerEvents: 'stroke' }}
              onMouseDown={(e) => onEdgeMouseDown(e, edge.index)}
              onMouseEnter={() => onEdgeHoverChange(edge.index)}
              onMouseLeave={() => onEdgeHoverChange(null)}
              onContextMenu={
                onEdgeContextMenu
                  ? (e) => onEdgeContextMenu(e, edge.index, edge.cardinal)
                  : undefined
              }
            />
          </g>
        );
      })}
      {vertices.map((vertex) => {
        const isHover = hoverState.vertexIndex === vertex.index;
        const isActive = activeState.vertexIndex === vertex.index;
        const fill = getVertexFillColor({ isActive, isHover });
        return (
          <rect
            key={`vertex-${vertex.index}`}
            className='transition-opacity'
            x={vertex.position.x - vertexHandleSize / 2}
            y={vertex.position.y - vertexHandleSize / 2}
            width={vertexHandleSize}
            height={vertexHandleSize}
            fill={fill}
            stroke='#ffffff'
            strokeWidth={vertexStrokeWidth}
            opacity={isSpaceHovered || isHover || isActive ? 1 : 0}
            style={{ cursor: vertex.cursor, pointerEvents: 'all' }}
            onMouseDown={(e) => onVertexMouseDown(e, vertex.index)}
            onMouseEnter={() => onVertexHoverChange(vertex.index)}
            onMouseLeave={() => onVertexHoverChange(null)}
            onContextMenu={
              onVertexContextMenu
                ? (e) => onVertexContextMenu(e, vertex.index)
                : undefined
            }
          />
        );
      })}
    </svg>
  );
}

export default PolygonHandles;
